import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Button, LinearProgress, ListSubheader, Menu, MenuItem, Typography } from '@material-ui/core';
import ArrowBack from '@material-ui/icons/ArrowBackOutlined';
import ArrowForward from '@material-ui/icons/ArrowForwardOutlined';
import CheckCircle from '@material-ui/icons/CheckCircleOutlined';
import DescriptionOutlined from '@material-ui/icons/DescriptionOutlined';
import ExpandMore from '@material-ui/icons/ExpandMoreOutlined';
import Save from '@material-ui/icons/SaveOutlined';
import { containedButton, textButton } from '../../../../utils/styles';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1)
    },
    progressHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1)
    },
    progressTitle: {
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    progressGroup: {
        display: 'block',
        color: theme.palette.text.secondary,
        fontSize: '0.7rem',
        lineHeight: 1.2,
        textAlign: 'left'
    },
    progressStep: {
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap'
    },
    progressBar: {
        height: 5,
        borderRadius: 4
    },
    actions: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: theme.spacing(1),
        '& > button': {
            minWidth: 0
        }
    },
    iconeConcluida: {
        color: theme.palette.tertiary.main,
        marginRight: theme.spacing(1)
    },
    editActions: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    },
    ...containedButton(theme, true, false),
    ...textButton(theme, true)
}));

/*
 * Navegação do cadastro no mobile: cada seção (antiga tab) é uma etapa própria.
 * `mobileSteps` é a lista plana produzida por buildMobileSteps.
 * `salvarBloqueado`: etapa só para consulta (fora do período de inscrição), o
 * Salvar fica desabilitado.
 */
export default function MobileRegistrationNavigation({
    mobileSteps = [],
    mobileIndex = 0,
    maxLiberado = 0,
    isEdit = false,
    enviando = false,
    salvarBloqueado = false,
    onBack,
    onCancel,
    onFinish,
    onNext,
    onStepChange,
    onSaveEdit
}) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);

    const total = mobileSteps.length;
    const current = mobileSteps[mobileIndex];
    const progress = total ? ((mobileIndex + 1) / total) * 100 : 0;
    const isFirst = mobileIndex <= 0;
    const isLast = mobileIndex >= total - 1;
    const temNavegacao = total > 1;

    const closeMenu = () => setAnchorEl(null);

    /* Livre para qualquer etapa já alcançada — quem valida o caminho é o wizard. */
    const handleStepChange = flatIndex => {
        closeMenu();
        if (flatIndex !== mobileIndex && flatIndex <= maxLiberado && onStepChange) {
            onStepChange(flatIndex);
        }
    };

    /* Itens do menu agrupados pela etapa macro a que pertencem. */
    const renderMenuItems = () => {
        const itens = [];
        let grupoAtual = null;

        mobileSteps.forEach(item => {
            if (!isEdit && item.stepId !== grupoAtual) {
                grupoAtual = item.stepId;
                itens.push(
                    <ListSubheader key={`grupo-${item.stepId}`} disableSticky>
                        {item.groupLabel}
                    </ListSubheader>
                );
            }

            itens.push(
                <MenuItem
                    key={`${item.stepId}-${item.sectionId}`}
                    selected={item.flatIndex === mobileIndex}
                    disabled={item.flatIndex > maxLiberado}
                    onClick={() => handleStepChange(item.flatIndex)}>
                    {item.flatIndex !== mobileIndex && item.flatIndex <= maxLiberado && (
                        <CheckCircle fontSize="small" className={classes.iconeConcluida} />
                    )}
                    {item.label}
                </MenuItem>
            );
        });

        return itens;
    };

    return (
        <div className={classes.root}>
            {temNavegacao && (
                <>
                    <div className={classes.progressHeader}>
                        <Button
                            size="small"
                            aria-label="Selecionar etapa"
                            aria-controls={anchorEl ? 'mobile-registration-steps' : undefined}
                            aria-haspopup="true"
                            onClick={event => setAnchorEl(event.currentTarget)}
                            endIcon={<ExpandMore />}>
                            <span>
                                {!isEdit && current && (
                                    <span className={classes.progressGroup}>{current.groupLabel}</span>
                                )}
                                <Typography className={classes.progressTitle} variant="body2">
                                    {current?.label}
                                </Typography>
                            </span>
                        </Button>
                        <Typography className={classes.progressStep} variant="caption">
                            {`Etapa ${mobileIndex + 1} de ${total}`}
                        </Typography>
                    </div>
                    {!isEdit && <LinearProgress className={classes.progressBar} variant="determinate" value={progress} />}
                </>
            )}

            <div className={`${classes.actions} ${isEdit && !temNavegacao ? classes.editActions : ''}`}>
                <Button fullWidth size="small" className={classes.textButton} onClick={onCancel}>Cancelar</Button>
                {temNavegacao && (
                    <Button
                        fullWidth
                        size="small"
                        className={classes.textButton}
                        onClick={onBack}
                        startIcon={<ArrowBack />}
                        disabled={isFirst}>
                        Voltar
                    </Button>
                )}
                {isEdit ? (
                    temNavegacao ? (
                        <Button
                            fullWidth
                            size="small"
                            className={classes.textButton}
                            onClick={onNext}
                            endIcon={<ArrowForward />}
                            disabled={isLast}>
                            Avançar
                        </Button>
                    ) : (
                        <Button fullWidth size="small" variant="contained" className={classes.containedButton} onClick={onSaveEdit} startIcon={<Save />} disabled={enviando || salvarBloqueado}>Salvar</Button>
                    )
                ) : isLast ? (
                    <Button fullWidth size="small" variant="contained" className={classes.containedButton} onClick={onFinish} startIcon={<DescriptionOutlined />} disabled={enviando}>Finalizar</Button>
                ) : (
                    <Button fullWidth size="small" variant="contained" className={classes.containedButton} onClick={onNext} endIcon={<ArrowForward />}>Avançar</Button>
                )}
            </div>

            {isEdit && temNavegacao && (
                <Button fullWidth size="small" variant="contained" className={classes.containedButton} onClick={onSaveEdit} startIcon={<Save />} disabled={enviando || salvarBloqueado}>
                    Salvar
                </Button>
            )}

            {temNavegacao && (
                <Menu id="mobile-registration-steps" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                    {renderMenuItems()}
                </Menu>
            )}
        </div>
    );
}

MobileRegistrationNavigation.propTypes = {
    mobileSteps: PropTypes.arrayOf(PropTypes.shape({
        flatIndex: PropTypes.number,
        stepIndex: PropTypes.number,
        stepId: PropTypes.string,
        groupLabel: PropTypes.string,
        sectionId: PropTypes.string,
        label: PropTypes.string
    })).isRequired,
    mobileIndex: PropTypes.number.isRequired,
    maxLiberado: PropTypes.number,
    isEdit: PropTypes.bool,
    enviando: PropTypes.bool,
    salvarBloqueado: PropTypes.bool,
    onBack: PropTypes.func,
    onCancel: PropTypes.func,
    onFinish: PropTypes.func,
    onNext: PropTypes.func,
    onStepChange: PropTypes.func,
    onSaveEdit: PropTypes.func
};
